import { parseLocation } from 'parse-address';

const DIRECTIONAL_ABBREVIATIONS = new Set(['N', 'S', 'E', 'W', 'NE', 'NW', 'SE', 'SW']);

export function toTitleCase(value) {
  if (!value) return value;
  return String(value)
    .toLowerCase()
    .replace(/\b([a-z])/g, c => c.toUpperCase());
}

export function normalizeDirectional(value) {
  if (!value) return value;
  const upper = String(value).toUpperCase();
  return DIRECTIONAL_ABBREVIATIONS.has(upper) ? upper : toTitleCase(value);
}

function countWords(value) {
  return String(value || '')
    .replace(/#\s+/g, '#')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .length;
}

export function normalizeAddressLines(address) {
  const normalizedInput = typeof address === 'string'
    ? address.replace(/\n/g, ', ').trim()
    : address;

  if (!normalizedInput) {
    return [];
  }

  const {
    number,
    prefix,
    street,
    type,
    sec_unit_type: secondaryUnitType,
    sec_unit_num: secondaryUnitNumber,
    city,
    state,
    zip
  } = parseLocation(normalizedInput) || {};
  const formattedSecondaryUnit = secondaryUnitType === '#'
    ? `#${secondaryUnitNumber || ''}`.trim()
    : [toTitleCase(secondaryUnitType), secondaryUnitNumber].filter(Boolean).join(' ');
  const line1 = [number, normalizeDirectional(prefix), toTitleCase(street), toTitleCase(type)]
    .concat(formattedSecondaryUnit ? [formattedSecondaryUnit] : [])
    .filter(Boolean)
    .join(' ');
  const line2Parts = [toTitleCase(city), state ? String(state).toUpperCase() : null, zip]
    .filter(Boolean);
  const line2 = line2Parts.length ? `${line2Parts[0]}, ${line2Parts.slice(1).join(' ')}` : '';

  // parse-address accepts partial street-only matches, so "8 Avenue Four"
  // otherwise becomes "8 Avenue". Preserve the input when parsing loses words.
  const isStreetOnlyInput = typeof address === 'string' && !/[,\n]/.test(address);
  const parserDroppedWords = countWords(line1) < countWords(normalizedInput);
  if (isStreetOnlyInput && !city && !state && !zip && parserDroppedWords) {
    return [toTitleCase(normalizedInput), ''];
  }

  if (line1 || line2) {
    return [line1, line2];
  }

  const fallbackLines = String(address || '')
    .split(/\n+/)
    .map(line => line.trim())
    .filter(Boolean);
  if (fallbackLines.length === 0) {
    return ['', ''];
  }
  if (fallbackLines.length === 1) {
    const commaLines = fallbackLines[0].split(',').map(line => line.trim()).filter(Boolean);
    if (commaLines.length > 1) {
      return [commaLines[0], commaLines.slice(1).join(', ')];
    }
    return [fallbackLines[0], ''];
  }
  return [fallbackLines[0], fallbackLines.slice(1).join(', ')];
}

export function normalizeAddressCasing(address) {
  const [line1, line2] = normalizeAddressLines(address);
  return [line1, line2].filter(Boolean).join(', ');
}
